import {
  Close,
  Mic,
  VolumeUp,
  PlayArrow,
  Pause,
  Chat,
  RecordVoiceOver,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  styled,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import { webChatSocket } from "../../socket";
import { useSnackbar } from "notistack";
import { TypeAnimation } from "react-type-animation";
import { webChatSocket, webRealtimeSocket } from "../../socket";

const PREFIX = "AIDialog";

const classes = {
  messagesContainer: `${PREFIX}-messagesContainer`,
  AIResponseContainer: `${PREFIX}-AIResponseContainer`,
  userTextContainer: `${PREFIX}-userTextContainer`,
};

const AIDialogContainer = styled("div")(({ theme }) => ({
  [`& .${classes.messagesContainer}`]: {
    height: "60vh",
    width: "70vw",
    maxWidth: "350px",
  },

  [`& .${classes.AIResponseContainer}`]: {
    height: "79%",
    overflowY: "auto",
    padding: "0.5rem",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.userTextContainer}`]: {
    marginTop: "1rem",
    height: "160px",
  },
}));

const AIDialog = ({
  open,
  onClose,
  configId,
  targetId,
  zoneId,
  introductionAI,
  speechToSpeech,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [inputMode, setInputMode] = useState(null); // null, "write" ou "talk"
  const [modeSelected, setModeSelected] = useState(false);
  const [isResponseCooldown, setIsResponseCooldown] = useState(false); // Cooldown pour éviter "conversation_already_has_active_response"
  const [expandedTranscriptions, setExpandedTranscriptions] = useState(
    new Set()
  ); // Pour tracker les transcriptions étendues

  const tmpAnswers = useRef([]);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef([]);
  const currentAudioRef = useRef(null);
  const isReceivingAudioRef = useRef(false);
  const recordedAudioBlobRef = useRef(null);
  const audioSavedRef = useRef(false); // Pour éviter de sauvegarder l'audio plusieurs fois
  const recordingStartTimeRef = useRef(null); // Pour tracker le temps d'enregistrement
  const cooldownTimerRef = useRef(null); // Timer pour le cooldown
  const shouldSendChunksRef = useRef(false); // Flag pour contrôler l'envoi des chunks
  const pendingTranscriptionRef = useRef(null); // Pour stocker temporairement la transcription utilisateur
  const pendingAITranscriptionRef = useRef(null); // Pour stocker temporairement la transcription IA

  const SAMPLE_RATE = 24000;
  const MIN_RECORDING_DURATION = 600; // Durée minimale en millisecondes (600 ms)
  const RESPONSE_COOLDOWN = 1000; // Délai de sécurité après la fin d'une réponse (1 seconde)
  const MAX_TRANSCRIPTION_LENGTH = 100; // Longueur maximale avant "afficher plus"

  // Fonctions utilitaires pour les transcriptions
  const truncateTranscription = (
    text,
    maxLength = MAX_TRANSCRIPTION_LENGTH
  ) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const toggleTranscriptionExpansion = (messageIndex) => {
    const newExpanded = new Set(expandedTranscriptions);
    if (newExpanded.has(messageIndex)) {
      newExpanded.delete(messageIndex);
    } else {
      newExpanded.add(messageIndex);
    }
    setExpandedTranscriptions(newExpanded);
  };

  // Utility functions for audio processing
  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const uint8ArrayToBase64 = (bytes) => {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const amplifyAudio = (pcmData, amplificationFactor = 2.5) => {
    const amplified = new Uint8Array(pcmData.length);
    for (let i = 0; i < pcmData.length - 1; i += 2) {
      let sample = pcmData[i] | (pcmData[i + 1] << 8);
      if (sample > 32767) sample -= 65536;
      sample = Math.round(sample * amplificationFactor);
      sample = Math.max(-32768, Math.min(32767, sample));
      if (sample < 0) sample += 65536;
      amplified[i] = sample & 0xff;
      amplified[i + 1] = (sample >> 8) & 0xff;
    }
    return amplified;
  };

  const pcm16ToWav = (pcm16Bytes, sampleRate = 24000, channels = 1) => {
    const byteRate = sampleRate * channels * 2;
    const blockAlign = channels * 2;
    const subChunk2Size = pcm16Bytes.length;
    const chunkSize = 36 + subChunk2Size;

    const header = new ArrayBuffer(44);
    const view = new DataView(header);

    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, chunkSize, true);
    view.setUint32(8, 0x57415645, false); // "WAVE"
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // Audio format (PCM)
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true); // 16 bits per sample
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, subChunk2Size, true);

    const wavFile = new Uint8Array(44 + pcm16Bytes.length);
    wavFile.set(new Uint8Array(header), 0);
    wavFile.set(pcm16Bytes, 44);

    return wavFile;
  };

  const audioBufferToPCM16FromFloat32 = (float32Array) => {
    const pcm16 = new Int16Array(float32Array.length);

    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    return new Uint8Array(pcm16.buffer);
  };

  const playCompleteAudio = useCallback(async () => {
    if (audioBufferRef.current.length === 0) {
      setIsProcessing(false);
      return;
    }

    try {
      isReceivingAudioRef.current = false;
      // NE PAS désactiver isProcessing ici, on attend que l'audio commence à jouer

      const totalLength = audioBufferRef.current.reduce(
        (sum, chunk) => sum + chunk.length,
        0
      );
      const combinedPcm = new Uint8Array(totalLength);

      let offset = 0;
      for (const chunk of audioBufferRef.current) {
        combinedPcm.set(chunk, offset);
        offset += chunk.length;
      }

      const amplifiedPcm = amplifyAudio(combinedPcm, 2.5);
      const wavBytes = pcm16ToWav(amplifiedPcm, SAMPLE_RATE, 1);
      const blob = new Blob([wavBytes], { type: "audio/wav" });

      // Sauvegarder la réponse audio dans les answers (une seule fois)
      if (!audioSavedRef.current) {
        const audioMessage = {
          type: "audio",
          audioBlob: blob,
          timestamp: Date.now(),
          isUser: false,
        };

        // Appliquer la transcription IA en attente si elle existe
        if (pendingAITranscriptionRef.current) {
          console.log(
            "Applying pending AI transcription:",
            pendingAITranscriptionRef.current
          );
          audioMessage.transcription = pendingAITranscriptionRef.current;
          pendingAITranscriptionRef.current = null; // Nettoyer après utilisation
        }

        setAnswers([...tmpAnswers.current, audioMessage]);
        tmpAnswers.current = [...tmpAnswers.current, audioMessage];
        audioSavedRef.current = true;
      }

      // Jouer l'audio
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audio.volume = 1.0;
      currentAudioRef.current = audio;

      const currentIndex = tmpAnswers.current.length - 1;

      // Quand l'audio commence à jouer, désactiver processing et activer playing
      audio.onplay = () => {
        setIsProcessing(false);
        setIsPlaying(true);
        setPlayingIndex(currentIndex);
      };

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);

        // Activer le cooldown de sécurité AVANT de désactiver isPlaying
        // pour éviter que le bouton ne se réactive momentanément
        console.log(
          `Starting ${RESPONSE_COOLDOWN}ms cooldown to prevent conversation_already_has_active_response`
        );
        setIsResponseCooldown(true);

        // Nettoyer le timer précédent s'il existe
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
        }

        // Désactiver le cooldown après le délai
        cooldownTimerRef.current = setTimeout(() => {
          setIsResponseCooldown(false);
          cooldownTimerRef.current = null;
        }, RESPONSE_COOLDOWN);

        // Maintenant on peut désactiver isPlaying et nettoyer
        setIsPlaying(false);
        setPlayingIndex(null);
        audioBufferRef.current = [];
        audioSavedRef.current = false; // Réinitialiser pour le prochain message
      };

      audio.onerror = (e) => {
        console.error("Error playing audio:", e);
        URL.revokeObjectURL(audioUrl);
        setIsProcessing(false);
        setIsPlaying(false);
        setPlayingIndex(null);
        audioBufferRef.current = [];
        audioSavedRef.current = false; // Réinitialiser pour le prochain message
      };

      await audio.play();
    } catch (e) {
      console.error("Error playing complete audio:", e);
      setIsProcessing(false);
      setIsPlaying(false);
      setPlayingIndex(null);
      audioBufferRef.current = [];
      audioSavedRef.current = false; // Réinitialiser pour le prochain message
    }
  }, [SAMPLE_RATE]);

  const startRecording = async () => {
    if (isRecording || isProcessing) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Créer AudioContext pour capturer l'audio brut
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Créer un ScriptProcessorNode pour capturer les échantillons PCM
      const bufferSize = 4096;
      const processor = audioContextRef.current.createScriptProcessor(
        bufferSize,
        1,
        1
      );

      // Réinitialiser pour le nouvel enregistrement
      recordedAudioBlobRef.current = null;
      const recordedChunks = [];
      recordingStartTimeRef.current = Date.now();
      shouldSendChunksRef.current = true; // Autoriser l'envoi des chunks

      // Créer aussi un MediaRecorder pour sauvegarder dans l'historique
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const completeBlob = new Blob(recordedChunks, { type: "audio/webm" });
        recordedAudioBlobRef.current = completeBlob;
      };

      // Traiter les échantillons audio en temps réel
      processor.onaudioprocess = (e) => {
        // Ne pas envoyer de chunks si on a arrêté l'envoi
        if (!shouldSendChunksRef.current) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = audioBufferToPCM16FromFloat32(inputData);
        const base64Chunk = uint8ArrayToBase64(pcm16);

        webRealtimeSocket.emit("audio-chunk", { chunk: base64Chunk });
      };

      // Connecter les nœuds
      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      // Sauvegarder les références
      mediaRecorderRef.current = {
        mediaRecorder,
        processor,
        source,
        stream,
        stop: () => {
          processor.disconnect();
          source.disconnect();
          mediaRecorder.stop();
          stream.getTracks().forEach((track) => track.stop());
        },
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (e) {
      console.error("Error starting recording:", e);
      enqueueSnackbar(t("toasts.microphoneError") || "Microphone error", {
        variant: "error",
      });
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    // Vérifier la durée de l'enregistrement
    const recordingDuration = Date.now() - recordingStartTimeRef.current;
    const isTooShort = recordingDuration < MIN_RECORDING_DURATION;

    if (mediaRecorderRef.current) {
      // Arrêter tout (processor, source, mediaRecorder, stream)
      mediaRecorderRef.current.stop();

      // Petit délai pour laisser les derniers chunks se propager, puis arrêter l'envoi
      setTimeout(() => {
        shouldSendChunksRef.current = false; // Arrêter l'envoi des chunks
      }, 50);

      // Attendre plus longtemps pour s'assurer que tous les chunks sont envoyés
      setTimeout(() => {
        if (recordedAudioBlobRef.current && !isTooShort) {
          // Ajouter l'audio envoyé aux answers seulement si assez long
          const userAudioMessage = {
            type: "audio",
            audioBlob: recordedAudioBlobRef.current,
            timestamp: Date.now(),
            isUser: true,
          };

          // Appliquer la transcription en attente si elle existe
          if (pendingTranscriptionRef.current) {
            console.log(
              "Applying pending transcription to user message:",
              pendingTranscriptionRef.current
            );
            userAudioMessage.transcription = pendingTranscriptionRef.current;
            pendingTranscriptionRef.current = null; // Nettoyer après utilisation
          }

          setAnswers([...tmpAnswers.current, userAudioMessage]);
          tmpAnswers.current = [...tmpAnswers.current, userAudioMessage];

          // Envoyer au backend seulement si assez long
          setIsProcessing(true);
          webRealtimeSocket.emit("end", { configId, targetId, zoneId });
          console.log("Recording stopped, waiting for response...");
        } else if (isTooShort) {
          console.log(
            `Recording too short: ${recordingDuration}ms, minimum is ${MIN_RECORDING_DURATION}ms`
          );
          enqueueSnackbar(
            t("toasts.recordingTooShort") ||
              "Recording too short, please hold longer",
            { variant: "warning" }
          );
        }
        mediaRecorderRef.current = null;
      }, 200); // Augmenté à 200ms pour laisser plus de temps aux chunks
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsRecording(false);
    recordingStartTimeRef.current = null;
  };

  const handleMouseDown = () => {
    if (!isProcessing && !isPlaying) {
      startRecording();
    }
  };

  const handleMouseUp = () => {
    if (isRecording) {
      stopRecording();
    }
  };

  const playAudioFromHistory = async (audioBlob, index) => {
    // Arrêter l'audio en cours si nécessaire
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Si on clique sur l'audio en cours de lecture, le mettre en pause
    if (playingIndex === index && isPlaying) {
      setIsPlaying(false);
      setPlayingIndex(null);
      return;
    }

    try {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = 1.0;
      currentAudioRef.current = audio;

      setIsPlaying(true);
      setPlayingIndex(index);

      audio.onended = () => {
        console.log("Audio playback completed");
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingIndex(null);
      };

      audio.onerror = (e) => {
        console.error("Error playing audio:", e);
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingIndex(null);
      };

      await audio.play();
    } catch (e) {
      console.error("Error playing audio from history:", e);
      setIsPlaying(false);
      setPlayingIndex(null);
    }
  };

  const handleSendText = () => {
    setAnswers([...answers, message]);
    tmpAnswers.current = [...answers, message];
    setMessage("");
    setLoading(true);
    webChatSocket.emit("message", { configId, message, targetId, zoneId }, () =>
      enqueueSnackbar(t("toasts.socketTimeout"), { variant: "error" })
    );
  };

  const handleModeSelection = (mode) => {
    setInputMode(mode);
    setModeSelected(true);
  };

  const scrollToBottom = useCallback(() => {
    const element = document.getElementById(`answersContainer-${configId}`);
    if (element && open)
      element.scrollTop = element.scrollHeight - element.clientHeight;
  }, [configId, open]);

  useEffect(() => {
    if (answers?.length > 0) scrollToBottom();
  }, [answers, scrollToBottom]);

  const handleAnswers = (res) => {
    setAnswers([...tmpAnswers.current, res]);
  };

  // useEffect pour l'initialisation à l'ouverture du dialog
  useEffect(() => {
    if (open) {
      const initialAnswers = introductionAI ? [introductionAI] : [];
      setAnswers(initialAnswers);
      tmpAnswers.current = initialAnswers;
      setLoading(false);
      setMessage("");

      // Réinitialiser la sélection du mode si speechToSpeech est activé
      if (speechToSpeech) {
        setInputMode(null);
        setModeSelected(false);
      } else {
        // Si pas de speechToSpeech, on est en mode write par défaut
        setInputMode("write");
        setModeSelected(true);
      }
    }
  }, [open, introductionAI, speechToSpeech]);

  // useEffect séparé pour la gestion des WebSockets
  useEffect(() => {
    if (!open || !modeSelected) {
      return; // Ne rien faire si le dialog n'est pas ouvert ou si aucun mode n'est sélectionné
    }

    // Connexion exclusive : soit Realtime (audio), soit Chat (texte)
    if (speechToSpeech && inputMode === "talk") {
      // Mode Speech-to-Speech : utiliser webRealtimeSocket uniquement
      webRealtimeSocket.connect();

      webRealtimeSocket.on("connect", () => {
        console.log("WebRealtimeSocket connected:", webRealtimeSocket.id);
        webRealtimeSocket.emit("start", { configId, targetId, zoneId });
      });

      webRealtimeSocket.on("started", (data) => {
        console.log("Server started:", data);
      });

      webRealtimeSocket.on("openai-event", (data) => {
        try {
          // Gérer les erreurs
          if (data?.type === "error") {
            console.error("OpenAI Realtime error:", data);

            // Si c'est l'erreur "conversation_already_has_active_response",
            // on attend simplement que la réponse se termine
            if (
              data?.error?.code === "conversation_already_has_active_response"
            ) {
              console.log("Waiting for previous response to finish...");
              return; // Ne pas réinitialiser les états, juste attendre
            }

            // Pour les autres erreurs, réinitialiser
            setIsProcessing(false);
            setIsPlaying(false);
            audioBufferRef.current = [];
            audioSavedRef.current = false;

            const errorMessage =
              data?.error?.message || "An error occurred with the AI response";
            enqueueSnackbar(errorMessage, { variant: "error" });
            return;
          }

          if (data?.status === "failed") {
            console.error("Response failed:", data?.status_details);
            setIsProcessing(false);
            setIsPlaying(false);
            audioBufferRef.current = [];
            audioSavedRef.current = false;

            const errorMessage =
              data?.status_details?.error?.message || "Response failed";
            enqueueSnackbar(errorMessage, { variant: "error" });
            return;
          }

          if (data?.type === "response.audio.delta") {
            if (!isReceivingAudioRef.current) {
              isReceivingAudioRef.current = true;
              audioBufferRef.current = [];
              audioSavedRef.current = false; // Réinitialiser pour le nouveau message
              console.log("Started receiving audio response");
            }

            const deltaB64 = data.delta;
            if (deltaB64) {
              try {
                const pcmBytes = base64ToUint8Array(deltaB64);
                audioBufferRef.current.push(pcmBytes);
              } catch (e) {
                console.error("Error decoding audio delta:", e);
              }
            }
          } else if (data?.type === "response.audio.done") {
            // Vérifier si la réponse a échoué
            if (data?.status === "failed") {
              console.error("Audio response failed:", data?.status_details);
              setIsProcessing(false);
              audioBufferRef.current = [];
              audioSavedRef.current = false;
              return;
            }

            // Audio terminé, on peut jouer
            if (audioBufferRef.current.length > 0 && !audioSavedRef.current) {
              console.log("Audio response completed, playing full audio");
              playCompleteAudio();
              // NE PAS désactiver isProcessing ici, audio.onplay le fera
            } else {
              console.log("Audio done but buffer empty");
              setIsProcessing(false);
              audioSavedRef.current = false;
            }
          } else if (data?.type === "response.done") {
            // Vérifier si la réponse a échoué
            if (data?.status === "failed") {
              console.error("Response failed:", data?.status_details);
              setIsProcessing(false);
              audioBufferRef.current = [];
              audioSavedRef.current = false;
              return;
            }

            // En mode audio, on ignore response.done et on attend response.audio.done
            // response.done peut arriver avant que les chunks audio ne commencent
            console.log("Response done event received, waiting for audio.done");
            // Ne rien faire - on laisse isProcessing actif jusqu'à response.audio.done
          }
        } catch (e) {
          console.error("Error processing openai-event:", e);
        }
      });

      webRealtimeSocket.on("answer-finished", (data) => {
        console.log("Answer finished", data);

        // Extraire la transcription de la réponse IA
        let aiTranscription = null;
        try {
          if (data?.detail?.response?.output?.[0]?.content?.[0]?.transcript) {
            aiTranscription =
              data.detail.response.output[0].content[0].transcript;
            console.log("AI transcription found:", aiTranscription);
          }
        } catch (e) {
          console.error("Error extracting AI transcription:", e);
        }

        if (audioBufferRef.current.length > 0 && !audioSavedRef.current) {
          // Stocker la transcription IA temporairement pour l'appliquer au message audio
          if (aiTranscription) {
            pendingAITranscriptionRef.current = aiTranscription;
          }
          playCompleteAudio();
          // NE PAS désactiver isProcessing ici, audio.onplay le fera
        } else {
          // Si pas d'audio mais qu'il y a une transcription, l'appliquer au dernier message IA
          if (aiTranscription) {
            console.log("Applying AI transcription to existing message");
            // Chercher le dernier message audio IA
            let aiAudioIndex = -1;
            for (let i = tmpAnswers.current.length - 1; i >= 0; i--) {
              const message = tmpAnswers.current[i];
              if (message?.type === "audio" && !message?.isUser) {
                aiAudioIndex = i;
                break;
              }
            }

            if (aiAudioIndex >= 0) {
              console.log(
                "Setting AI transcription to message index:",
                aiAudioIndex
              );
              tmpAnswers.current[aiAudioIndex].transcription = aiTranscription;
              setAnswers([...tmpAnswers.current]);
              console.log("AI transcription set successfully");
            }
          }

          console.log("Answer finished but no audio to play");
          setIsProcessing(false);
        }
      });

      webRealtimeSocket.on("transcription", (data) => {
        console.log("Received transcription:", data);
        console.log("Current tmpAnswers:", tmpAnswers.current);

        // Chercher le dernier message audio UTILISATEUR (pas l'IA)
        let userAudioIndex = -1;
        for (let i = tmpAnswers.current.length - 1; i >= 0; i--) {
          const message = tmpAnswers.current[i];
          if (message?.type === "audio" && message?.isUser === true) {
            userAudioIndex = i;
            break;
          }
        }

        console.log("User audio message index:", userAudioIndex);

        if (userAudioIndex >= 0) {
          const transcriptionText = data.text || data.transcript || data;
          console.log("Setting transcription:", transcriptionText);
          tmpAnswers.current[userAudioIndex].transcription = transcriptionText;
          setAnswers([...tmpAnswers.current]);
          console.log("Transcription set successfully");
        } else {
          // Si pas encore de message utilisateur audio, stocker la transcription temporairement
          console.log(
            "No user audio message found, storing transcription for later"
          );
          pendingTranscriptionRef.current =
            data.text || data.transcript || data;
        }
      });

      webRealtimeSocket.on("disconnect", () => {});
    } else if (modeSelected) {
      // Mode Chat (texte) : utiliser webChatSocket uniquement
      webChatSocket.connect();
      webChatSocket.on("answer", (res) => {
        handleAnswers(res);
        setLoading(false);
      });
    }

    return () => {
      // Cleanup selon le mode
      if (speechToSpeech && inputMode === "talk") {
        // Nettoyage mode Realtime
        webRealtimeSocket.off("connect");
        webRealtimeSocket.off("started");
        webRealtimeSocket.off("openai-event");
        webRealtimeSocket.off("answer-finished");
        webRealtimeSocket.off("transcription");
        webRealtimeSocket.off("disconnect");
        webRealtimeSocket.disconnect();

        // Clean up audio resources
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.stop();
          mediaRecorderRef.current.stream
            .getTracks()
            .forEach((track) => track.stop());
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
        }
        audioBufferRef.current = [];

        // Nettoyer le timer de cooldown
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      } else {
        // Nettoyage mode Chat
        webChatSocket.off("answer");
        webChatSocket.disconnect();
      }
    };
  }, [
    open,
    modeSelected,
    speechToSpeech,
    inputMode,
    configId,
    targetId,
    zoneId,
    playCompleteAudio,
    enqueueSnackbar,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      aria-labelledby="ai-dialog-webxr"
      aria-describedby="ai-dialog-webxr-desc"
    >
      <AIDialogContainer style={{ overflow: "hidden" }}>
        <DialogTitle sx={{ paddingBottom: "0" }}>
          {t("player.buttons.letstalk")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 10,
            top: 10,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ overflow: "unset", minWidth: "250px" }}>
          {speechToSpeech && !modeSelected ? (
            // Afficher seulement les boutons de sélection de mode
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "32px 16px",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Chat />}
                onClick={() => handleModeSelection("write")}
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  padding: "12px 24px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {t("player.buttons.writeMode") || "Écrire"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RecordVoiceOver />}
                onClick={() => handleModeSelection("talk")}
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  padding: "12px 24px",
                  textTransform: "none",
                  fontSize: "16px",
                }}
              >
                {t("player.buttons.talkMode") || "Discuter"}
              </Button>
            </Box>
          ) : (
            // Afficher l'interface normale
            <Box className={classes.messagesContainer}>
              <Paper
                elevation={5}
                className={classes.AIResponseContainer}
                id={`answersContainer-${configId}`}
              >
                {answers?.map((item, index) => {
                  const isAudio =
                    typeof item === "object" && item.type === "audio";
                  // Pour les messages audio, utiliser item.isUser, sinon utiliser l'index
                  const isUserMessage = isAudio
                    ? item.isUser === true
                    : index % 2 === 1;

                  return (
                    <Box
                      key={`answer-${index + 1}-${configId}`}
                      sx={{ display: "flex" }}
                    >
                      {isUserMessage && <Box sx={{ width: "45%" }} />}
                      <Paper
                        elevation={12}
                        sx={{
                          width: "200px",
                          padding: "0.5rem",
                          marginBottom: "0.75rem",
                          cursor: isAudio ? "pointer" : "default",
                          backgroundColor: isUserMessage
                            ? isAudio && playingIndex === index
                              ? "#3d3636"
                              : "#4f4848"
                            : isAudio && playingIndex === index
                            ? "rgba(255, 255, 255, 0.3)"
                            : "#f0f0f0",
                          transition: "background-color 0.3s ease",
                          "&:hover": isAudio
                            ? {
                                backgroundColor: isUserMessage
                                  ? "#3d3636"
                                  : "rgba(255, 255, 255, 0.2)",
                              }
                            : {},
                        }}
                        onClick={() =>
                          isAudio && playAudioFromHistory(item.audioBlob, index)
                        }
                      >
                        {isAudio ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: isUserMessage ? "#fff" : "#000",
                              }}
                            >
                              {playingIndex === index && isPlaying ? (
                                <Pause sx={{ fontSize: "24px" }} />
                              ) : (
                                <PlayArrow sx={{ fontSize: "24px" }} />
                              )}
                              <VolumeUp sx={{ fontSize: "20px" }} />
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontFamily: "Lato, sans-serif",
                                }}
                              >
                                {playingIndex === index && isPlaying
                                  ? t("player.buttons.playing") || "Playing..."
                                  : t("player.buttons.audioMessage") ||
                                    "Audio message"}
                              </span>
                            </Box>
                            {item.transcription && (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontFamily: "Lato, sans-serif",
                                  color: isUserMessage
                                    ? "rgba(255,255,255,0.8)"
                                    : "rgba(0,0,0,0.7)",
                                  fontStyle: "italic",
                                  marginTop: "4px",
                                  paddingTop: "4px",
                                  borderTop: `1px solid ${
                                    isUserMessage
                                      ? "rgba(255,255,255,0.2)"
                                      : "rgba(0,0,0,0.1)"
                                  }`,
                                  lineHeight: 1.3,
                                  wordBreak: "break-word",
                                }}
                              >
                                {item.transcription.length >
                                  MAX_TRANSCRIPTION_LENGTH &&
                                !expandedTranscriptions.has(index) ? (
                                  <>
                                    "{truncateTranscription(item.transcription)}
                                    "
                                    <Box
                                      component="span"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Empêcher la lecture audio
                                        toggleTranscriptionExpansion(index);
                                      }}
                                      sx={{
                                        color: isUserMessage
                                          ? "rgba(255,255,255,0.6)"
                                          : "rgba(0,0,0,0.5)",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        marginLeft: "8px",
                                        "&:hover": {
                                          color: isUserMessage
                                            ? "rgba(255,255,255,0.9)"
                                            : "rgba(0,0,0,0.8)",
                                        },
                                      }}
                                    >
                                      afficher plus
                                    </Box>
                                  </>
                                ) : (
                                  <>
                                    "{item.transcription}"
                                    {item.transcription.length >
                                      MAX_TRANSCRIPTION_LENGTH && (
                                      <Box
                                        component="span"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Empêcher la lecture audio
                                          toggleTranscriptionExpansion(index);
                                        }}
                                        sx={{
                                          color: isUserMessage
                                            ? "rgba(255,255,255,0.6)"
                                            : "rgba(0,0,0,0.5)",
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                          marginLeft: "8px",
                                          "&:hover": {
                                            color: isUserMessage
                                              ? "rgba(255,255,255,0.9)"
                                              : "rgba(0,0,0,0.8)",
                                          },
                                        }}
                                      >
                                        afficher moins
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <TypeAnimation
                            splitter={(str) => str.split(/(?= )/)} // split by words
                            sequence={[item, () => scrollToBottom()]}
                            wrapper="p"
                            key={item}
                            speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                            omitDeletionAnimation={true}
                            style={{
                              fontSize: "16px",
                              display: "block",
                              fontFamily: "Lato, sans-serif",
                              color: isUserMessage ? "#fff" : "#000",
                              margin: 0,
                              whiteSpace: "pre-line",
                            }}
                            cursor={false}
                            repeat={0}
                          >
                            {item}
                          </TypeAnimation>
                        )}
                      </Paper>
                      {!isUserMessage && <Box sx={{ width: "45%" }} />}
                    </Box>
                  );
                })}
              </Paper>
              {(!speechToSpeech || inputMode === "write") && (
                <Box className={classes.userTextContainer}>
                  <TextField
                    sx={{ width: "100%" }}
                    value={message}
                    multiline
                    rows={2}
                    placeholder={t("player.buttons.enterAIMessage")}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Box>
              )}
              {speechToSpeech && inputMode === "talk" && (
                <Box className={classes.userTextContainer}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <IconButton
                      onMouseDown={handleMouseDown}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={handleMouseDown}
                      onTouchEnd={handleMouseUp}
                      disabled={isProcessing || isPlaying || isResponseCooldown}
                      sx={{
                        backgroundColor: isRecording
                          ? "error.main"
                          : isProcessing
                          ? "primary.main" // Garder la couleur primaire pendant processing
                          : isPlaying
                          ? "rgba(255, 0, 0, 0.3)"
                          : "primary.main",
                        color: "white",
                        boxShadow: isRecording
                          ? "0 0 15px 3px rgba(255, 0, 0, 0.4)"
                          : "none",
                        transition: "all 0.3s ease",
                        width: "56px",
                        height: "56px",
                        "&:hover": {
                          backgroundColor: isRecording
                            ? "error.dark"
                            : "primary.dark",
                        },
                        "&:disabled": {
                          backgroundColor:
                            isProcessing || isResponseCooldown
                              ? "primary.main" // Garder la couleur primaire visible pendant processing et cooldown
                              : "rgba(128, 128, 128, 0.3)",
                          color: "white",
                          cursor: "wait", // Curseur d'attente
                          opacity: isResponseCooldown ? 0.7 : 1, // Légèrement transparent pendant cooldown
                        },
                      }}
                    >
                      {isProcessing ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : isResponseCooldown ? (
                        <CircularProgress
                          size={24}
                          sx={{ color: "white", opacity: 0.5 }}
                        />
                      ) : (
                        <Mic />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!speechToSpeech || modeSelected ? (
            inputMode === "write" || !speechToSpeech ? (
              <Button
                onClick={handleSendText}
                disabled={message.length <= 0 || loading}
              >
                {loading ? (
                  <CircularProgress size={28} />
                ) : (
                  t("player.buttons.sendButton")
                )}
              </Button>
            ) : null
          ) : null}
        </DialogActions>
      </AIDialogContainer>
    </Dialog>
  );
};

export default AIDialog;
